import { createFileRoute } from "@tanstack/react-router";
import {
  addMonths,
  format,
  isEqual,
  parse,
  startOfMonth,
  subDays,
  subMonths,
} from "date-fns";
import { ja } from "date-fns/locale";
import { useCallback, useEffect, useState } from "react";
import type {
  GetRedemptionsResponse,
  GetRedemptionsResponse_Redemption,
} from "schema/gen/es/chiikipoint/companyconsole/v2/service_pb";
import { css } from "../../../styled-system/css";
import { formatNumber } from "../../libs/utils";

export const Route = createFileRoute("/company/redemptions")({
  loader: async ({ context: { client } }) => {
    return { client };
  },
  component: RouteComponent,
});

// 月1回締めのテナントは期間がその月そのものなので、ラベルを出さず従来どおりの表示にする
function formatPeriod(redemption: GetRedemptionsResponse_Redemption) {
  const start = redemption.periodStart?.toDate();
  const end = redemption.periodEnd?.toDate();
  if (!start || !end || (start.getDate() === 1 && isEqual(end, addMonths(start, 1)))) {
    return null;
  }
  // periodEnd は締め期間の終端の翌日0時（半開区間の上限）なので、表示は1日戻す
  return `${format(start, "M月d日")}〜${format(subDays(end, 1), "M月d日")}`;
}

function RedemptionSection({
  redemption,
}: {
  redemption?: GetRedemptionsResponse_Redemption;
}) {
  const period = redemption ? formatPeriod(redemption) : null;
  const storeRedemptions = redemption?.storeRedemptions ?? [];

  return (
    <div className={css({ mb: "32px" })}>
      <div
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        })}
      >
        {period && (
          <div className={css({ fontSize: "14px", fontWeight: "bold" })}>
            {period}
          </div>
        )}
        <div
          className={css({
            display: "flex",
            alignItems: "baseline",
            gap: "12px",
          })}
        >
          <div>振込額合計</div>
          <div className={css({ fontSize: "2xl", fontWeight: "bold" })}>
            {formatNumber(Number(redemption?.totalAmount ?? 0))}円
          </div>
        </div>
        <div
          className={css({
            color: "text.secondary",
            fontSize: "12px",
          })}
        >
          締め処理完了後、所定のスケジュールに従って振込が実行されます。
        </div>
      </div>

      {storeRedemptions.length > 0 && (
        <div
          className={css({
            mt: "12px",
            bg: "white",
            borderRadius: "10px",
            overflow: "hidden",
            border: "1px solid",
            borderColor: "border.secondary",
          })}
        >
          <table
            className={css({
              width: "100%",
              borderCollapse: "collapse",
            })}
          >
            <thead>
              <tr
                className={css({
                  borderBottom: "1px solid",
                  borderColor: "border.secondary",
                  bg: "white",
                })}
              >
                <th
                  className={css({
                    py: "12px",
                    px: "16px",
                    textAlign: "left",
                    fontWeight: "normal",
                    color: "text.secondary",
                  })}
                >
                  店舗名
                </th>
                <th
                  className={css({
                    py: "12px",
                    px: "16px",
                    textAlign: "right",
                    fontWeight: "normal",
                    color: "text.secondary",
                  })}
                >
                  金額
                </th>
              </tr>
            </thead>
            <tbody>
              {storeRedemptions.map((storeRedemption) => (
                <tr
                  key={storeRedemption.storeName}
                  className={css({
                    borderBottom: "1px solid",
                    borderColor: "border.secondary",
                    _hover: {
                      bg: "#F4F4FE",
                    },
                    _last: {
                      borderBottom: "none",
                    },
                  })}
                >
                  <td
                    className={css({
                      py: "12px",
                      px: "16px",
                      wordBreak: "break-all",
                      maxWidth: "320px",
                    })}
                  >
                    {storeRedemption.storeName}
                  </td>
                  <td
                    className={css({
                      py: "12px",
                      px: "16px",
                      textAlign: "right",
                    })}
                  >
                    {formatNumber(Number(storeRedemption.amount))}円
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RouteComponent() {
  const { client } = Route.useLoaderData();
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => {
    return startOfMonth(new Date());
  });
  const [redemptionData, setRedemptionData] =
    useState<GetRedemptionsResponse | null>(null);
  const [error, setError] = useState(false);

  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const date = subMonths(new Date(), i);
    const startDate = startOfMonth(date);
    return {
      value: format(startDate, "yyyy/MM"),
      label: format(startDate, "yyyy年M月", { locale: ja }),
    };
  });

  const fetchRedemptions = useCallback(
    async (date: Date) => {
      setLoading(true);
      setRedemptionData(null);
      setError(false);

      try {
        const yearMonth = format(date, "yyyyMM");
        const response = await client.getRedemptions({
          yearMonth,
        });
        setRedemptionData(response);
      } catch (err) {
        console.error("Failed to fetch redemptions:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [client],
  );

  const handleDateChange = (value: string) => {
    const date = parse(value, "yyyy/MM", new Date());
    setSelectedDate(startOfMonth(date));
  };

  useEffect(() => {
    fetchRedemptions(selectedDate);
  }, [selectedDate, fetchRedemptions]);

  const redemptions = redemptionData?.redemptions ?? [];

  return (
    <div
      className={css({
        px: "40px",
        py: "24px",
        pb: "100px",
        h: "100%",
        overflow: "auto",
      })}
    >
      <h1
        className={css({
          fontSize: "18px",
          fontWeight: "bold",
          mb: "20px",
        })}
      >
        振込履歴
      </h1>

      <>
        <select
          value={format(selectedDate, "yyyy/MM")}
          onChange={(e) => handleDateChange(e.target.value)}
          className={css({
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid",
            borderColor: "border.secondary",
            marginBottom: "20px",
            width: "200px",
            fontSize: "14px",
            bg: "white",
          })}
        >
          {monthOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {loading ? (
          <div
            className={css({
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: "40px",
              color: "text.secondary",
            })}
          >
            <div>読み込み中...</div>
          </div>
        ) : error ? (
          <div
            role="alert"
            className={css({
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: "40px",
              color: "text.accentError",
            })}
          >
            振込履歴の取得に失敗しました。管理者にお問い合わせください。
          </div>
        ) : (
          // 締めが1件も無い月は、従来どおり「振込額合計 0円」を出す
          <>
            {redemptions.length === 0 ? (
              <RedemptionSection />
            ) : (
              redemptions.map((redemption) => (
                <RedemptionSection
                  key={String(redemption.periodStart?.seconds)}
                  redemption={redemption}
                />
              ))
            )}
          </>
        )}
      </>
    </div>
  );
}
